import { EncounterStatus, EncounterStatusV2 } from "../services/utils";

/** Encounter-like shape with schedule status on root or nested schedule. */
export type EncounterScheduleStatusSource = {
  scheduleStatus?: string;
  encounterSchedule?: { scheduleStatus?: string } | null;
};

/** Includes clinical encounter row status for Open promotion (procedure room + RFV). */
export type EncounterOpenPromotionSource = EncounterScheduleStatusSource & {
  encounterStatus?: string;
};

function normalizeStatusLower(raw: string | null | undefined): string {
  return String(raw ?? "")
    .trim()
    .toLowerCase();
}

/** Scheduled, Unconfirmed, or Confirmed — eligible to promote to Open. */
export function isPreOpenClinicalStatus(raw: string | null | undefined): boolean {
  const status = normalizeStatusLower(raw);
  return (
    status === EncounterStatus.Scheduled.toLowerCase() ||
    status === EncounterStatus.Unconfirmed.toLowerCase() ||
    status === EncounterStatus.Confirmed.toLowerCase() ||
    status === EncounterStatusV2.Scheduled.toLowerCase() ||
    status === EncounterStatusV2.Unconfirmed.toLowerCase() ||
    status === EncounterStatusV2.Confirmed.toLowerCase()
  );
}

/** Resolve schedule status from nested `encounterSchedule` (encounter_schedule row) first. */
export function resolveEncounterScheduleRowStatus(
  appointment: EncounterScheduleStatusSource | null | undefined
): string {
  if (!appointment) {
    return "";
  }
  const fromSchedule = (appointment.encounterSchedule?.scheduleStatus ?? "")
    .toString()
    .trim();
  if (fromSchedule) {
    return fromSchedule;
  }
  return resolveEncounterScheduleStatus(appointment);
}

/** Resolve schedule status from encounter root or nested `encounterSchedule`. */
export function resolveEncounterScheduleStatus(
  appointment: EncounterScheduleStatusSource | null | undefined
): string {
  if (!appointment) {
    return "";
  }
  const root = (appointment.scheduleStatus ?? "").toString().trim();
  if (root) {
    return root;
  }
  return (appointment.encounterSchedule?.scheduleStatus ?? "")
    .toString()
    .trim();
}

export function isEncounterScheduleSubmitted(
  scheduleStatus: string | undefined
): boolean {
  return (scheduleStatus ?? "").toString().trim().toLowerCase() === "submitted";
}

export function isEncounterScheduleOpen(
  appointment: EncounterScheduleStatusSource | null | undefined
): boolean {
  return (
    resolveEncounterScheduleRowStatus(appointment).trim().toLowerCase() === "open"
  );
}

/** Schedule row (encounter_schedule) still Scheduled / Unconfirmed / Confirmed. */
export function isEncounterSchedulePreOpen(
  appointment: EncounterScheduleStatusSource | null | undefined
): boolean {
  return isPreOpenClinicalStatus(resolveEncounterScheduleRowStatus(appointment));
}

export function isEncounterStatusPreOpen(
  appointment: EncounterOpenPromotionSource | null | undefined
): boolean {
  return isPreOpenClinicalStatus(appointment?.encounterStatus);
}

export function isEligibleForOpenPromotion(
  appointment: EncounterOpenPromotionSource | null | undefined
): boolean {
  if (!appointment) {
    return false;
  }
  if (isEncounterScheduleSubmitted(resolveEncounterScheduleRowStatus(appointment))) {
    return false;
  }
  return (
    isEncounterSchedulePreOpen(appointment) ||
    isEncounterStatusPreOpen(appointment)
  );
}

const OPEN_OR_LATER_STATUS = new Set([
  "open",
  "in progress",
  "inprogress",
  "checked in",
  "checkedin",
  "completed",
  "complete",
  "submitted",
  "sign req",
  "signreq",
  "ready for billing",
  "readyforbilling",
  "cancelled",
  "no show",
  "noshow",
]);

/** True when Open promotion should not run again (already Open or past it). */
export function hasEncounterReachedOpenOrBeyond(
  appointment: EncounterOpenPromotionSource | null | undefined
): boolean {
  if (!appointment) {
    return false;
  }
  const labels = [
    resolveEncounterScheduleRowStatus(appointment),
    appointment.encounterStatus,
    appointment.scheduleStatus,
  ];
  return labels.some((raw) => {
    const s = normalizeStatusLower(raw);
    if (!s) {
      return false;
    }
    if (s === "open") {
      return true;
    }
    return OPEN_OR_LATER_STATUS.has(s);
  });
}

export type PromoteScheduleStatusToOpenPatch = {
  id: string;
  scheduleStatus?: string;
  encounterStatus?: string;
};

/**
 * Build a patch that sets schedule and/or encounter status to Open when still
 * pre-Open (Scheduled, Unconfirmed, Confirmed).
 */
export function buildPromoteScheduleStatusToOpenPatch(
  encounterId: string,
  appointment: EncounterOpenPromotionSource | null | undefined
): PromoteScheduleStatusToOpenPatch | null {
  const id = String(encounterId || "").trim();
  if (!id || !appointment) {
    return null;
  }
  const patch: PromoteScheduleStatusToOpenPatch = { id };
  let hasField = false;
  if (isEncounterStatusPreOpen(appointment)) {
    patch.encounterStatus = EncounterStatus.Open;
    hasField = true;
  }
  if (isEncounterSchedulePreOpen(appointment)) {
    patch.scheduleStatus = EncounterStatusV2.Open;
    hasField = true;
  }
  return hasField ? patch : null;
}

/**
 * When submitting to billing, move encounter_schedule to Submitted
 * (Open, Sign Req, etc.) unless it is already Submitted.
 */
export function resolveScheduleStatusAfterSubmitToBilling(
  appointment: EncounterScheduleStatusSource | null | undefined
): string | undefined {
  const current = resolveEncounterScheduleRowStatus(appointment);
  if (isEncounterScheduleSubmitted(current)) {
    return undefined;
  }
  return EncounterStatusV2.Submitted;
}

export type SubmitEncounterToBillingPatch = {
  id: string;
  encounterStatus: string;
  scheduleStatus?: string;
};

export function buildSubmitEncounterToBillingPatch(
  encounterId: string,
  appointment: EncounterScheduleStatusSource | null | undefined
): SubmitEncounterToBillingPatch {
  const patch: SubmitEncounterToBillingPatch = {
    id: encounterId,
    encounterStatus: EncounterStatus.Complete,
  };
  const nextScheduleStatus = resolveScheduleStatusAfterSubmitToBilling(appointment);
  if (nextScheduleStatus) {
    patch.scheduleStatus = nextScheduleStatus;
  }
  return patch;
}

/** Prefer Submitted from patch when re-fetch lags behind encounter_schedule update. */
export function resolveScheduleStatusAfterSubmit(
  fresh: EncounterScheduleStatusSource | null | undefined,
  patchScheduleStatus?: string
): string {
  const fromPatch = String(patchScheduleStatus ?? "").trim();
  const fromFresh = resolveEncounterScheduleRowStatus(fresh);
  if (isEncounterScheduleSubmitted(fromPatch)) {
    return fromPatch;
  }
  if (isEncounterScheduleSubmitted(fromFresh)) {
    return fromFresh;
  }
  return fromFresh || fromPatch;
}
